import { useRef, useEffect, useState } from 'react';
import { Send, Mic, MicOff, Lightbulb } from 'lucide-react';

const ChatInput = ({ currentMessage, setCurrentMessage, onSend, sending, isListening, onToggleListening, isSessionActive, hintState }) => {
  const inputRef = useRef(null);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    if (isSessionActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSessionActive]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend(e);
  };

  const handleHint = async () => {
    if (hintState?.onRequestHint) {
      const hint = await hintState.onRequestHint();
      if (hint) setShowHint(true);
    }
  };

  return (
    <div className="flex space-x-4">
      <div className="flex-1">
        {/* Hint display */}
        {showHint && hintState?.currentHint && (
          <div
            data-testid="hint-display"
            className="mb-2 p-3 rounded-lg border-l-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ backgroundColor: '#FFF7ED', borderLeftColor: '#F59E0B' }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                <div>
                  <p className="text-sm font-medium text-gray-800">{hintState.currentHint.text}</p>
                  {hintState.currentHint.detail && (
                    <p className="text-xs text-gray-500 mt-0.5">{hintState.currentHint.detail}</p>
                  )}
                  <span className="text-xs mt-1 inline-block px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    {hintState.currentHint.source}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowHint(false)}
                className="text-gray-400 hover:text-gray-600 text-xs flex-shrink-0"
                data-testid="hint-close-btn"
              >
                &times;
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex space-x-2">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              placeholder={isListening ? 'Parlez maintenant...' : 'Tapez votre réponse...'}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-all resize-none min-h-[80px] ${
                isListening
                  ? 'border-red-500 bg-red-50 focus:ring-2 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-2'
              }`}
              style={!isListening ? { '--tw-ring-color': '#2E6CF5' } : {}}
              disabled={sending}
              data-testid="message-input"
              rows="3"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
          </div>

          {/* Hint button - only for incendie */}
          {hintState?.available && (
            <button
              type="button"
              onClick={handleHint}
              disabled={sending || hintState.allUsed}
              className="px-3 py-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed relative group"
              style={{
                backgroundColor: hintState.allUsed ? '#E5E7EB' : '#FEF3C7',
                color: hintState.allUsed ? '#9CA3AF' : '#92400E',
              }}
              data-testid="hint-button"
              title={hintState.allUsed ? 'Tous les indices ont été donnés' : 'Demander un indice'}
            >
              <Lightbulb className="w-5 h-5" />
              {hintState.hintsUsed > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: '#F59E0B' }}
                >
                  {hintState.hintsUsed}
                </span>
              )}
            </button>
          )}

          <button
            type="submit"
            disabled={sending || !currentMessage.trim()}
            className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#002768' }}
            data-testid="send-message-button"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>

        {isListening && (
          <div className="mt-2 flex items-center space-x-2 text-sm text-red-600">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span>Enregistrement en cours... Parlez clairement</span>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center justify-center">
        <button
          type="button"
          onClick={onToggleListening}
          disabled={sending}
          className={`w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all shadow-lg ${
            isListening
              ? 'bg-red-500 text-white animate-pulse shadow-red-300'
              : 'text-white hover:shadow-xl'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          style={!isListening ? { background: 'linear-gradient(135deg, #002768, #2E6CF5)' } : {}}
          data-testid="microphone-button"
          title={isListening ? "Arrêter l'enregistrement" : "Commencer l'enregistrement vocal"}
        >
          {isListening ? <MicOff className="w-10 h-10" /> : <Mic className="w-10 h-10" />}
        </button>
        <span className="mt-2 text-xs text-center text-gray-600 font-medium">
          {isListening ? 'Arrêter' : 'Micro'}
        </span>
      </div>
    </div>
  );
};

export default ChatInput;
